import { FormEvent, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminShell } from '../components/layout/AdminShell';
import { FieldHelp, FieldHelpGroup } from '../components/FieldHelp';
import { Modal } from '../components/Modal';
import { StatusPill } from '../components/StatusPill';
import { useToast } from '../components/ToastHost';
import { fetchMe, requestPhotoUploadUrl, updateMe } from '../lib/adminApi';
import { accountHelp } from '../lib/fieldHelpCopy';

const MAX_PHOTO_BYTES = 5 * 1024 * 1024;

function isAllowedPhoto(file: File): boolean {
  if (file.type === 'image/png' || file.type === 'image/jpeg' || file.type === 'image/jpg') {
    return true;
  }
  // Some OS file pickers leave type empty — fall back to extension.
  const name = file.name.toLowerCase();
  return name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg');
}

function photoContentType(file: File): string {
  if (file.type === 'image/png') return 'image/png';
  if (file.type === 'image/jpeg' || file.type === 'image/jpg') return 'image/jpeg';
  const name = file.name.toLowerCase();
  if (name.endsWith('.png')) return 'image/png';
  return 'image/jpeg';
}

export function AccountPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { success } = useToast();
  const meQuery = useQuery({ queryKey: ['me'], queryFn: fetchMe });
  const [editOpen, setEditOpen] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!meQuery.data) return;
    setDisplayName(meQuery.data.display_name || '');
    setPhone(meQuery.data.phone || '');
    setAddress(meQuery.data.address || '');
    setBirthDate(meQuery.data.birth_date || '');
  }, [meQuery.data]);

  function openEdit() {
    const me = meQuery.data;
    if (!me) return;
    setError(null);
    setPhotoFile(null);
    setDisplayName(me.display_name || '');
    setPhone(me.phone || '');
    setAddress(me.address || '');
    setBirthDate(me.birth_date || '');
    setEditOpen(true);
  }

  function onPhotoChange(file: File | null) {
    setError(null);
    if (!file) {
      setPhotoFile(null);
      return;
    }
    if (!isAllowedPhoto(file)) {
      setPhotoFile(null);
      setError('Photo must be PNG or JPEG');
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      setPhotoFile(null);
      setError('Photo must be 5MB or smaller');
      return;
    }
    setPhotoFile(file);
  }

  async function handleSave(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSaving(true);
    try {
      if (photoFile) {
        const contentType = photoContentType(photoFile);
        const { upload_url } = await requestPhotoUploadUrl(contentType);
        const put = await fetch(upload_url, {
          method: 'PUT',
          headers: { 'Content-Type': contentType },
          body: photoFile,
        });
        if (!put.ok) throw new Error(`Photo upload failed (${put.status})`);
      }
      await updateMe({
        display_name: displayName,
        phone,
        address,
        birth_date: birthDate,
      });
      success(photoFile ? 'Profile and photo updated' : 'Profile updated');
      setEditOpen(false);
      setPhotoFile(null);
      await queryClient.invalidateQueries({ queryKey: ['me'] });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  const me = meQuery.data;

  return (
    <AdminShell title="My account" subtitle={me?.email}>
      <div className="panel">
        <div className="panel-toolbar">
          <p className="panel-toolbar__meta">Your staff profile</p>
          <div className="panel-toolbar__actions">
            {me?.can_edit_account ? (
              <button className="primary-button" type="button" disabled={!me} onClick={openEdit}>
                Edit profile
              </button>
            ) : (
              <p className="hint-text">
                Profile editing requires admin-granted account permission (or your one-time first
                edit).
              </p>
            )}
          </div>
        </div>

        {meQuery.isLoading ? <p>Loading…</p> : null}
        {meQuery.isError ? (
          <div className="form-error">
            {meQuery.error instanceof Error ? meQuery.error.message : 'Failed to load profile'}
          </div>
        ) : null}
        {error && !editOpen ? <div className="form-error">{error}</div> : null}

        {me ? (
          <div className="admin-meta-row">
            {me.photo_url ? (
              <img
                src={me.photo_url}
                alt="Profile"
                className="profile-photo"
              />
            ) : (
              <div className="profile-photo profile-photo--empty" aria-label="No photo">
                no photo
              </div>
            )}
            <div>
              <p className="hint-text">Display name</p>
              <strong>{me.display_name || '—'}</strong>
            </div>
            <div>
              <p className="hint-text">Email</p>
              <strong>{me.email}</strong>
            </div>
            <div>
              <p className="hint-text">Phone</p>
              <strong>{me.phone || '—'}</strong>
            </div>
            <div>
              <p className="hint-text">Address</p>
              <strong>{me.address || '—'}</strong>
            </div>
            <div>
              <p className="hint-text">Birth date</p>
              <strong>{me.birth_date || '—'}</strong>
            </div>
            <div>
              <p className="hint-text">Role</p>
              <strong>{me.role}</strong>
            </div>
            <div>
              <p className="hint-text">Status</p>
              <StatusPill status={me.status} />
            </div>
          </div>
        ) : null}
      </div>

      {editOpen && me ? (
        <Modal
          title="Edit profile"
          onClose={() => setEditOpen(false)}
          closeOnBackdrop={false}
          wide
        >
          <form className="login-form" onSubmit={handleSave}>
            <FieldHelpGroup>
              <FieldHelp label="Display name" help={accountHelp.displayName}>
                <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
              </FieldHelp>
              <FieldHelp label="Phone" help={accountHelp.phone}>
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  type="tel"
                  autoComplete="tel"
                />
              </FieldHelp>
              <FieldHelp label="Address" help={accountHelp.address}>
                <input
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  autoComplete="street-address"
                />
              </FieldHelp>
              <FieldHelp label="Birth date" help={accountHelp.birthDate}>
                <input
                  value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                  type="date"
                />
              </FieldHelp>
              <FieldHelp label="Photo (PNG or JPEG, max 5MB)" help={accountHelp.photo}>
                <input
                  type="file"
                  accept="image/png,image/jpeg,.png,.jpg,.jpeg"
                  onChange={(e) => onPhotoChange(e.target.files?.[0] ?? null)}
                />
              </FieldHelp>
            </FieldHelpGroup>
            {photoFile ? (
              <p className="hint-text">Selected: {photoFile.name}</p>
            ) : me.photo_url ? (
              <p className="hint-text">Leave empty to keep the current photo.</p>
            ) : null}
            {error ? <div className="form-error">{error}</div> : null}
            <div className="modal-card__actions">
              <button className="primary-button" type="submit" disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                className="ghost-button"
                type="button"
                disabled={saving}
                onClick={() => setEditOpen(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      ) : null}
    </AdminShell>
  );
}
